import { Button } from 'antd';
import { ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DollarCircleFilled } from '@ant-design/icons';
import { isPageInIFrame } from '@common/helpers/isPageInIFrame';
import { useQueryStringParameters } from '@common/hooks/useQueryStringParameters/useQueryStringParameters';
import { GetEstimateDto } from '@common/models/save-estimate-dto';

export interface PaymentAvailableProps {
    paymentUrl: string;
    estimate: GetEstimateDto;
    children?: ReactNode;
}

export interface PaymentButtonProps {
    paymentUrl?: string;
    disabled?: boolean;
}

/**
 *
 * @param paymentUrl this is the paymentUrl from either the status or fetchedEstimate use: getBestPaymentUrl()
 * @param estimate the fetched estimate to pay
 * @param children allow for child elements
 * @constructor Creates a Payment Available message and button to pay now, including logic for breaking out of an iframe
 */
export const PaymentAvailable = ({ paymentUrl, estimate, children }: PaymentAvailableProps): JSX.Element => {
    const { t } = useTranslation();

    return (
        <div className="description-container">
            <div style={{ marginBottom: '1rem' }}>
                {t('NOTIFICATIONS.FETCH_ESTIMATE.READY_TO_PAY.DESC', { estimateNumber: estimate.estimateNumber })}
            </div>
            <PaymentButton paymentUrl={paymentUrl} />
            {children ? <div style={{ margin: '1rem 0' }}>{children}</div> : null}
        </div>
    );
};
/**
 *
 * @param paymentUrl this is the paymentUrl from either the status or fetchedEstimate use: getBestPaymentUrl()
 *  it may not be the actual destination used (could be overridden)
 * @constructor Creates a Payment button to pay now, including logic for breaking out of an iframe
 */
export const PaymentButton = ({ paymentUrl, disabled }: PaymentButtonProps): JSX.Element => {
    const { t } = useTranslation();
    const { locationCheckoutUrl } = useQueryStringParameters();

    /**
     * This is the most appropriate URL for a payment click
     * * If locationCheckoutUrl has been explicitly given, use that
     * * If we're in an iFrame and paymentUrl is relative link, use the best available parent
     * * If we're not in an iFrame use the paymentUrl given
     */
    const clickUrl = useMemo(() => {
        if (!paymentUrl) {
            return; // if there's no paymentUrl from status or fetchedEstimate, ignore everything
        }
        if (locationCheckoutUrl) {
            return locationCheckoutUrl; // explicitly given url, use it
        }
        const isFullUrl = URL.canParse(paymentUrl);
        if (isPageInIFrame()) {
            if (!isFullUrl) {
                return;
            }
            return paymentUrl;
        }
        return isFullUrl ? paymentUrl : new URL(window.location.origin + paymentUrl).toString();
    }, [locationCheckoutUrl, paymentUrl]);

    const onPayNowClick = useCallback(
        (_event: React.MouseEvent<HTMLElement>) => {
            window.open(clickUrl, isPageInIFrame() ? '_blank' : undefined);
        },
        [clickUrl]
    );

    return (
        <Button
            disabled={disabled || false}
            className={`payment-button ${disabled ? 'payment-button__disabled' : ''}`}
            onClick={onPayNowClick}
            type="primary"
        >
            <DollarCircleFilled /> {t('ORDER_SUMMARY.ESTIMATE_SECTION.PAYMENT_BUTTON')}
        </Button>
    );
};
